import React, { useState, useEffect } from 'react';
import { View, StyleSheet, ScrollView, KeyboardAvoidingView, Platform, ActivityIndicator } from 'react-native';
import { TextInput, Button, Title, Text, Snackbar, HelperText, useTheme } from 'react-native-paper';
import { getUserProfile, updateUserProfile } from '../../api';
import { ErrorMessage } from '../../components';
import { isValidEmail, isValidPhone } from '../../utils';
import { useAuth } from '../../context/AuthContext';

const EditProfileScreen = ({ navigation, route }) => {
  const theme = useTheme();
  const { user: authUser, updateUserInfo } = useAuth();
  const initialProfile = route.params?.profile;
  
  // State variables
  const [formData, setFormData] = useState({
    name: '',
    surname: '',
    email: '',
    phone: '',
  });
  
  // UI state variables
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(!initialProfile);
  const [snackbarVisible, setSnackbarVisible] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  
  // Fetch profile data if not provided
  useEffect(() => {
    if (initialProfile) {
      setFormData({
        name: initialProfile.name || '',
        surname: initialProfile.surname || '',
        email: initialProfile.email || '',
        phone: initialProfile.phone || '',
      });
    } else if (authUser) {
      // AuthContext'ten gelen kullanıcı bilgilerini kullan
      setFormData({
        name: authUser.name || '',
        surname: authUser.surname || '',
        email: authUser.email || '',
        phone: authUser.phone || authUser.phone_number || '',
      });
      setInitialLoading(false);
    } else {
      fetchUserProfile();
    }
  }, [initialProfile, authUser]);
  
  // Fetch user profile
  const fetchUserProfile = async () => {
    setInitialLoading(true);
    
    try {
      const response = await getUserProfile();
      const profile = response.user;
      
      if (profile) {
        setFormData({
          name: profile.name || '',
          surname: profile.surname || '',
          email: profile.email || '',
          phone: profile.phone || profile.phone_number || '',
        });
      }
    } catch (err) {
      console.error('Error fetching user profile:', err);
      setSnackbarMessage('Profil bilgileri yüklenirken bir hata oluştu');
      setSnackbarVisible(true);
    } finally {
      setInitialLoading(false);
    }
  };
  
  // Input change handler
  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
    // Clear error when user types
    if (errors[field]) {
      setErrors({ ...errors, [field]: '' });
    }
  };
  
  // Form validation
  const validateForm = () => {
    const newErrors = {};
    
    // Name validation
    if (!formData.name.trim()) {
      newErrors.name = 'Ad gereklidir';
    }
    
    // Surname validation
    if (!formData.surname.trim()) {
      newErrors.surname = 'Soyad gereklidir';
    }
    
    // Email validation
    if (!formData.email.trim()) {
      newErrors.email = 'E-posta gereklidir';
    } else if (!isValidEmail(formData.email)) {
      newErrors.email = 'Geçerli bir e-posta giriniz';
    }
    
    // Phone validation (optional)
    if (formData.phone && !isValidPhone(formData.phone)) {
      newErrors.phone = 'Geçerli bir telefon numarası giriniz';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  // Form submission
  const handleSubmit = async () => {
    if (!validateForm()) return;
    
    setIsLoading(true);
    
    try {
      // Güncellenecek veri
      const updateData = {
        name: formData.name,
        surname: formData.surname,
        email: formData.email,
        phone: formData.phone,
      };

      const response = await updateUserProfile(updateData);
      
      // Auth Context'teki kullanıcı bilgilerini güncelle
      if (response && response.success) {
        await updateUserInfo({
          name: formData.name,
          surname: formData.surname,
          email: formData.email,
          phone_number: formData.phone || authUser.phone_number,
        });
      }
      
      setSnackbarMessage('Profil başarıyla güncellendi');
      setSnackbarVisible(true);
      
      // Navigate back after successful update
      setTimeout(() => {
        navigation.goBack();
      }, 1500);
    } catch (err) {
      console.error('Error updating profile:', err);
      setSnackbarMessage('Profil güncellenirken bir hata oluştu');
      setSnackbarVisible(true);
    } finally {
      setIsLoading(false);
    }
  };
  
  // Loading state
  if (initialLoading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color="rgb(125, 76, 236)" />
        <Text style={styles.loadingText}>Profil bilgileri yükleniyor...</Text>
      </View>
    );
  }
  
  return (
    <KeyboardAvoidingView
      behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      style={styles.container}
    >
      <ScrollView 
        contentContainerStyle={styles.scrollContent}
        keyboardShouldPersistTaps="handled"
      >
        <View style={styles.headerContainer}>
          <Title style={styles.title}>Profil Düzenle</Title>
          <Text style={styles.subtitle}>Kişisel bilgilerinizi güncelleyin</Text>
        </View>
        
        <View style={styles.formContainer}>
          {/* Kişisel Bilgiler */}
          <TextInput
            label="Ad *"
            value={formData.name}
            onChangeText={(value) => handleChange('name', value)}
            mode="outlined"
            error={!!errors.name}
            style={styles.input}
            left={<TextInput.Icon name="account" />}
            disabled={isLoading}
          />
          {errors.name ? <HelperText type="error">{errors.name}</HelperText> : null}
          
          <TextInput
            label="Soyad *"
            value={formData.surname}
            onChangeText={(value) => handleChange('surname', value)}
            mode="outlined"
            error={!!errors.surname}
            style={styles.input}
            left={<TextInput.Icon name="account" />}
            disabled={isLoading}
          />
          {errors.surname ? <HelperText type="error">{errors.surname}</HelperText> : null}
          
          <TextInput
            label="E-posta *"
            value={formData.email}
            onChangeText={(value) => handleChange('email', value)}
            mode="outlined"
            keyboardType="email-address"
            error={!!errors.email}
            style={styles.input}
            left={<TextInput.Icon name="email" />}
            disabled={isLoading}
          />
          {errors.email ? <HelperText type="error">{errors.email}</HelperText> : null}
          
          <TextInput
            label="Telefon"
            value={formData.phone}
            onChangeText={(value) => handleChange('phone', value)}
            mode="outlined"
            keyboardType="phone-pad"
            error={!!errors.phone}
            style={styles.input}
            left={<TextInput.Icon name="phone" />}
            disabled={isLoading}
          />
          {errors.phone ? <HelperText type="error">{errors.phone}</HelperText> : null}
          
          {/* Kaydet Butonu */}
          <Button
            mode="contained"
            onPress={handleSubmit}
            style={styles.button}
            loading={isLoading}
            disabled={isLoading}
          >
            Kaydet
          </Button>
        </View>
      </ScrollView>
      
      <Snackbar
        visible={snackbarVisible}
        onDismiss={() => setSnackbarVisible(false)}
        duration={3000}
        style={{backgroundColor: 'rgb(125, 76, 236)'}}
        theme={{colors: {surface: '#fff'}}}
        action={{
          label: 'Tamam',
          onPress: () => setSnackbarVisible(false),
          color: '#fff'
        }}
      >
        {snackbarMessage}
      </Snackbar>
    </KeyboardAvoidingView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: 'rgb(232, 162, 249)',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    marginTop: 10,
    fontSize: 16,
    color: 'rgb(90, 50, 180)',
  },
  scrollContent: {
    flexGrow: 1,
    padding: 20,
    backgroundColor: 'rgba(125, 76, 236, 0.05)',
    borderRadius: 12,
    margin: 16,
  },
  headerContainer: {
    alignItems: 'center',
    marginVertical: 20,
  },
  title: {
    fontSize: 24,
    fontWeight: 'bold',
    color: 'rgb(125, 76, 236)',
  },
  subtitle: {
    fontSize: 16,
    color: 'rgb(90, 50, 180)',
    marginTop: 4,
  },
  formContainer: {
    width: '100%',
  },
  input: {
    marginBottom: 8,
  },
  button: {
    marginTop: 24,
    height: 50,
    justifyContent: 'center',
    backgroundColor: 'rgb(125, 76, 236)',
    marginBottom: 20,
  },
});

export default EditProfileScreen;
